/**
 * API de i18n minimalista (sem dependência externa).
 *
 * - t(key, vars?)          → string traduzida (fallback en → key literal)
 * - t.exists(key)          → boolean
 * - setLang(code)          → troca idioma + aplica no DOM + salva no localStorage
 * - getLang()              → código atual
 * - getAvailableLangs()    → lista de {code, label, short}
 * - applyTranslations(root?)→ percorre data-i18n e data-i18n-placeholder
 * - initI18n()             → detecta lang do localStorage ou browser, retorna a escolhida
 * - onLangChange(fn)       → subscribe a mudanças de idioma
 *
 * O idioma atual fica em window.__LANG e em localStorage 'compress.lang'.
 *
 * Detecção: localStorage (escolha do user) → navigator.language (browser) → en (default)
 * Isso significa que user BR vê pt-BR, user americano vê en, etc.
 * Default é en porque o site tem AdSense gringo (mercado principal).
 */

import ptBR from './locales/pt-BR.js';
import en from './locales/en.js';
import es from './locales/es.js';
import de from './locales/de.js';
import fr from './locales/fr.js';

const STORAGE_KEY = 'compress.lang';
const DEFAULT_LANG = 'en';

const LOCALES = {
  'pt-BR': ptBR,
  en,
  es,
  de,
  fr,
};

const META = {
  'pt-BR': { label: 'Português', short: 'PT' },
  en: { label: 'English', short: 'EN' },
  es: { label: 'Español', short: 'ES' },
  de: { label: 'Deutsch', short: 'DE' },
  fr: { label: 'Français', short: 'FR' },
};

let currentLang = DEFAULT_LANG;

function resolveLocale(code) {
  if (LOCALES[code]) return code;
  const base = String(code || '').split('-')[0];
  if (LOCALES[base]) return base;
  return DEFAULT_LANG;
}

function getStoredLang() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && LOCALES[v]) return v;
    if (v) return resolveLocale(v);
  } catch {
    // localStorage indisponível (modo privado, quota cheia, etc)
  }
  return null;
}

/** Interpola {var} numa string. */
function interpolate(template, vars) {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));
}

/** Inicializa com lang do localStorage, browser, ou default. Retorna a lang escolhida. */
export function initI18n() {
  const stored = getStoredLang();
  if (stored) {
    currentLang = stored;
  } else if (typeof navigator !== 'undefined' && navigator.language) {
    currentLang = resolveLocale(navigator.language);
  } else {
    currentLang = DEFAULT_LANG;
  }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = currentLang.split('-')[0];
  }
  return currentLang;
}

/** t() — retorna a string traduzida. */
export function t(key, vars) {
  const locale = LOCALES[currentLang] || LOCALES[DEFAULT_LANG];
  const fallback = LOCALES[DEFAULT_LANG];
  const str = (locale && locale[key]) || (fallback && fallback[key]) || key;
  return interpolate(str, vars);
}

/** Boolean: a chave existe na locale atual? */
export function hasKey(key) {
  const locale = LOCALES[currentLang] || LOCALES[DEFAULT_LANG];
  return Boolean(locale && locale[key]);
}

/** Troca idioma, aplica no DOM e persiste. */
export function setLang(code) {
  const resolved = resolveLocale(code);
  if (!LOCALES[resolved]) return;
  currentLang = resolved;
  try {
    localStorage.setItem(STORAGE_KEY, resolved);
  } catch {
    // localStorage indisponível — lang fica em memória apenas
  }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = resolved.split('-')[0];
    applyTranslations(document);
    document.dispatchEvent(new CustomEvent('lang:change', { detail: { lang: resolved } }));
  }
}

/** Subscribe a mudanças de idioma. Retorna função pra cancelar. */
export function onLangChange(fn) {
  if (typeof document === 'undefined') {
    return () => {};
  }
  document.addEventListener('lang:change', (e) => fn(e.detail.lang));
  return () => document.removeEventListener('lang:change', (e) => fn(e.detail.lang));
}

/** Idioma atual. */
export function getLang() {
  return currentLang;
}

/** Lista de idiomas disponíveis (pra montar o seletor). */
export function getAvailableLangs() {
  return Object.keys(LOCALES).map((code) => ({
    code,
    label: META[code]?.label || code,
    short: META[code]?.short || code,
  }));
}

/**
 * Percorre o DOM e troca textos com data-i18n / data-i18n-placeholder / data-i18n-aria.
 */
export function applyTranslations(root = (typeof document !== 'undefined' ? document : null)) {
  if (!root) return;
  root.querySelectorAll('[data-i18n]').forEach((el) => {
    const key = el.getAttribute('data-i18n');
    if (key) el.textContent = t(key);
  });
  root.querySelectorAll('[data-i18n-placeholder]').forEach((el) => {
    const key = el.getAttribute('data-i18n-placeholder');
    if (key) el.setAttribute('placeholder', t(key));
  });
  root.querySelectorAll('[data-i18n-aria]').forEach((el) => {
    const key = el.getAttribute('data-i18n-aria');
    if (key) el.setAttribute('aria-label', t(key));
  });
  root.querySelectorAll('[data-i18n-title]').forEach((el) => {
    const key = el.getAttribute('data-i18n-title');
    if (key) el.setAttribute('title', t(key));
  });
}

/** Retorna todas as chaves de todas as locales (pra check de paridade). */
export function getAllKeys() {
  const set = new Set();
  for (const locale of Object.values(LOCALES)) {
    for (const key of Object.keys(locale)) {
      set.add(key);
    }
  }
  return [...set].sort();
}

/** Retorna as chaves de uma locale específica. */
export function getKeys(code) {
  const resolved = resolveLocale(code);
  return Object.keys(LOCALES[resolved] || {}).sort();
}

// Expor pra debug em dev (window.__LANG)
if (typeof window !== 'undefined') {
  Object.defineProperty(window, '__LANG', {
    get: () => currentLang,
  });
}
